import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";
import API from "@src/API";
import { usePersonSkillsQuery } from "../../query/usePersonSkills";

function pick(obj, ...keys) {
  for (const k of keys) {
    if (obj?.[k] != null && obj[k] !== "") return obj[k];
  }
  return undefined;
}

function formatReleaseDate(value) {
  if (!value) return "";
  const raw = String(value);
  const dt = DateTime.fromISO(raw);
  if (!dt.isValid) {
    const fallback = new Date(value);
    if (Number.isNaN(fallback.getTime())) return "";
    return DateTime.fromJSDate(fallback).toFormat("MM/yyyy");
  }
  return dt.toFormat("MM/yyyy");
}

function toCacheSkill(raw, id) {
  return {
    ID: raw.ID ?? raw.id ?? id,
    Name: pick(raw, "Name", "name") || "",
    ShortName: pick(raw, "ShortName", "shortName") || "",
    Description: pick(raw, "Description", "description") || "",
    CategoryID: pick(raw, "CategoryID", "categoryID") || 0,
    CategoryName: pick(raw, "CategoryName", "categoryName") || "",
    ReleaseDate: pick(raw, "ReleaseDate", "releaseDate") || null,
    WikiUrl: pick(raw, "WikiUrl", "wikiUrl") || "",
    Version: pick(raw, "Version", "version") || "",
    Status: pick(raw, "Status", "status") || "",
    ParentID: pick(raw, "ParentID", "parentID") || 0,
    ParentSkillName: pick(raw, "ParentSkillName", "parentSkillName") || "",
  };
}

/**
 * Summary card for a catalog skill (used at the top of person-skill edit).
 * Prefers an explicit skill prop, then Skills Query cache, then GET /Skill.
 */
export default function SkillBrief({ skillID, skill: skillProp, usedVersions, onEdit }) {
  const id = Number(skillProp?.ID ?? skillProp?.id ?? skillID) || 0;
  const { data: skillsData } = usePersonSkillsQuery();
  const fromQuery =
    (skillsData?.skills || []).find((s) => Number(s.ID ?? s.id) === id) || null;
  const [skill, setSkill] = useState(() => skillProp || fromQuery);
  const fetchedForId = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const local = skillProp || fromQuery;
    if (local) setSkill(local);

    const hasRelease = !!(pick(local, "ReleaseDate", "releaseDate"));
    if (!(id > 0) || hasRelease || fetchedForId.current === id) {
      return () => {
        cancelled = true;
      };
    }

    fetchedForId.current = id;

    (async () => {
      const resp = await API.getAsync("/Skill?ID=" + id);
      if (cancelled || !resp?.ok || !resp.jsonData) return;
      const raw = Array.isArray(resp.jsonData) ? resp.jsonData[0] : resp.jsonData;
      if (!raw) return;
      setSkill(toCacheSkill(raw, id));
    })();

    return () => {
      cancelled = true;
    };
  }, [skillID, id, skillProp, fromQuery]);

  if (!skill || typeof skill !== "object") {
    return <div className="mobileCard">Loading skill…</div>;
  }

  const displayId = Number(pick(skill, "ID", "id")) || id;
  const name = pick(skill, "Name", "name") || "Skill";
  const shortName = pick(skill, "ShortName", "shortName") || name;
  const releaseLabel = formatReleaseDate(pick(skill, "ReleaseDate", "releaseDate"));
  const versionLine = Array.isArray(usedVersions)
    ? usedVersions.filter(Boolean).join(", ")
    : "";

  return (
    <div className="mobileCard">
      <div className="header skill-brief-header">
        {typeof onEdit === "function" && displayId > 0 ? (
          <button
            type="button"
            className="skillLink skill-brief-edit"
            data-testid={`skill-edit-${displayId}`}
            onClick={() => onEdit(displayId)}
          >
            Skill - {shortName}
          </button>
        ) : (
          <span className="skill-brief-title">Skill - {shortName}</span>
        )}
        {displayId > 0 ? (
          <span className="skill-brief-id" data-testid="skill-brief-skill-id">
            {displayId}
          </span>
        ) : null}
      </div>
      <div className="body">
        {versionLine ? (
          <div data-testid="skill-brief-versions">
            <div className="label">Versions used: </div>
            {versionLine}
          </div>
        ) : null}
        <div>
          <div className="label">Release Date: </div>
          {releaseLabel || "N/A"}
        </div>
        <div>
          <div className="label">Category: </div>
          {pick(skill, "CategoryName", "categoryName") || "—"}
        </div>
        <div>
          <div className="label">Description: </div>
          {pick(skill, "Description", "description") || ""}
        </div>
      </div>
    </div>
  );
}
